// src/data/achievementsData.ts - СБАЛАНСИРОВАННАЯ ЛОГИКА ДОСТИЖЕНИЙ v2

import { formatInTimeZone } from 'date-fns-tz';
import { subDays } from 'date-fns';

/**
 * ВЕРСИЯ КАТАЛОГА - при изменении структуры инкрементировать
 * Используется для миграции и пересчёта прогресса
 *
 * История изменений:
 * v2 → v3: Удалены достижения 'upgraded_05' и 'upgraded_10' (stage_upgrade)
 *          Миграция автоматически очистит их из сохранённых данных пользователей
 */
export const ACHIEVEMENTS_VERSION = 3;

export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  unlocked: boolean;
  unlockedAt?: string;
  howToUnlock: string;
  progress?: number; // текущий прогресс к цели
  trigger: {
    type:
      | 'applications'
      | 'streak'
      | 'perfect_week'
      | 'perfect_weeks_count' // новый: накопление идеальных недель
      | 'comeback_count' // новый: подсчёт повторений камбэков
      | 'complete_course' // новый: все запланированные даты отмечены
      | 'share'
      | 'referral'
      | 'feedback'
      | 'stage_upgrade';
    target?: number;
    condition?: string;
  };
}

/**
 * ✅ СБАЛАНСИРОВАННЫЙ НАБОР ДОСТИЖЕНИЙ (3 уровня сложности)
 * Все достижения реалистичны при графике 3 раза в неделю
 */
export const ACHIEVEMENTS: Achievement[] =[
  // ========== ЛЁГКИЕ (первые 2-3 недели) ==========

  {
    id: 'first_step',
    name: 'Первый шаг',
    description: 'Первое применение ретинола',
    icon: 'PartyPopper', // 🎉
    unlocked: false,
    howToUnlock: 'Отметьте своё первое применение в календаре',
    trigger: { type: 'applications', target: 1 },
  },

  {
    id: 'week_habit',
    name: 'Неделя привычки',
    description: '3 применения за неделю',
    icon: 'Flame', // 🔥
    unlocked: false,
    howToUnlock: 'Выполните 3 запланированных применения за 7 дней',
    trigger: { type: 'perfect_week', condition: 'checkPerfectWeek' },
  },

  {
    id: 'share_first',
    name: 'Вдохновитель',
    description: 'Поделился прогрессом',
    icon: 'Share2', // 📤
    unlocked: false,
    howToUnlock: 'Нажмите кнопку "Поделиться прогрессом" в профиле',
    trigger: { type: 'share', target: 1 },
  },

  // ========== СРЕДНИЕ (1-3 месяца) ==========

  {
    id: 'dozen_apps',
    name: '12 применений',
    description: 'Месяц регулярного ухода',
    icon: 'Heart', // 💜
    unlocked: false,
    howToUnlock: 'Отметьте 12 применений в календаре (≈4 недели при графике 3 раза в неделю)',
    trigger: { type: 'applications', target: 12 },
  },

  {
    id: 'four_perfect_weeks',
    name: 'Без пропусков',
    description: '4 идеальные недели подряд',
    icon: 'Star', // ⭐
    unlocked: false,
    howToUnlock: 'Выполняйте все запланированные применения 4 недели подряд',
    trigger: { type: 'perfect_weeks_count', target: 4, condition: 'checkConsecutivePerfectWeeks' },
  },

  {
    id: 'comeback_twice',
    name: 'Возвращение в строй',
    description: 'Вернулся к плану 2 раза',
    icon: 'Sprout', // 🌱
    unlocked: false,
    howToUnlock: 'После пропуска возобновите применения 2 раза',
    trigger: { type: 'comeback_count', target: 2, condition: 'checkComebackCount' },
  },

  // ========== ДОЛГОСРОЧНЫЕ (3-6 месяцев) ==========

  {
    id: 'month_care',
    name: 'Месяц заботы',
    description: '30 применений выполнено',
    icon: 'Gem', // 💎
    unlocked: false,
    howToUnlock: 'Отметьте 30 применений (≈10 недель при графике 3 раза в неделю)',
    trigger: { type: 'applications', target: 30 },
  },

  {
    id: 'halfway_there',
    name: 'Половина пути',
    description: '45 применений выполнено',
    icon: 'Trophy', // 🏆
    unlocked: false,
    howToUnlock: 'Продолжайте применять ретинол - Вы на полпути к мастерству (≈15 недель)',
    trigger: { type: 'applications', target: 45 },
  },

  {
    id: 'expert',
    name: 'Эксперт ретинола',
    description: '60 применений выполнено',
    icon: 'Crown', // 👑
    unlocked: false,
    howToUnlock: 'Отметьте 60 применений - Вы стали настоящим экспертом (≈20 недель)',
    trigger: { type: 'applications', target: 60 },
  },

  // ========== СОЦИАЛЬНЫЕ ==========

  {
    id: 'invited_friend',
    name: 'Друг красоты',
    description: 'Пригласил друга',
    icon: 'Gift', // 🎁
    unlocked: false,
    howToUnlock: 'Отправь реферальную ссылку другу через профиль',
    trigger: { type: 'referral', target: 1 },
  },

  {
    id: 'gave_feedback',
    name: 'Голос заботы',
    description: 'Оставил обратную связь',
    icon: 'MessageCircle', // 💬
    unlocked: false,
    howToUnlock: 'Нажмите "Обратная связь" в профиле и поделитесь впечатлениями',
    trigger: { type: 'feedback', target: 1 },
  },

  // ✅ УДАЛЕНЫ достижения за переход на этапы 0.5% и 1.0% (v3):
  // - 'upgraded_05': Уверенный шаг
  // - 'upgraded_10': Мастер адаптации
  // Причина: Излишняя детализация, дублируют stage progression

  // ========== МАКСИМАЛЬНОЕ ДОСТИЖЕНИЕ ==========

  {
    id: 'legend',
    name: 'Легенда ухода',
    description: 'Полный курс ретинола пройден',
    icon: 'Trophy', // 🏆
    unlocked: false,
    howToUnlock: 'Отметьте все запланированные применения и пройдите полный курс ретинола от Geltek',
    trigger: { type: 'complete_course' },
  },
];

// ========== ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ ==========

/**
 * ✅ FIX: Получает дату в формате YYYY-MM-DD с учетом user.timezone
 * Использует date-fns-tz для корректной работы при смене timezone
 */
function getDateInUserTimezone(date: Date, timezone: string): string {
  return formatInTimeZone(date, timezone, 'yyyy-MM-dd');
}

/** Получает последние N дней от текущей даты в указанном timezone */
export function getLastNDays(n: number, timezone: string = 'UTC'): string[] {
  const dates: string[] = [];
  const today = new Date();

  for (let i = 0; i < n; i++) {
    const date = subDays(today, i);
    dates.push(getDateInUserTimezone(date, timezone)); // ✅ FIX: используем user timezone
  }
  return dates;
}

/** Вычисляет текущий стрик (последовательные запланированные дни) */
export function calculateCurrentStreak(
  plannedDates: string[],
  applications: Array<{ date: string; marked: boolean }>,
  timezone: string = 'UTC'
): number {
  if (plannedDates.length === 0) return 0;

  const today = getDateInUserTimezone(new Date(), timezone); // ✅ FIX: используем user timezone
  const sortedPlanned = [...plannedDates].sort().reverse();

  let streak = 0;

  for (const plannedDate of sortedPlanned) {
    if (plannedDate > today) continue;
    const isMarked = applications.some((a) => a.date === plannedDate && a.marked);
    if (isMarked) streak++;
    else break;
  }

  return streak;
}

/**
 * ✅ Проверяет "идеальную неделю":
 * ДОЛЖНО быть минимум 3 запланированных дня за последние 7 дней,
 * и все они — отмечены.
 */
export function checkPerfectWeek(
  plannedDates: string[],
  applications: Array<{ date: string; marked: boolean }>,
  timezone: string = 'UTC'
): boolean {
  const last7Days = getLastNDays(7, timezone); // ✅ FIX: передаем timezone
  const plannedInWeek = plannedDates.filter((d) => last7Days.includes(d));

  if (plannedInWeek.length < 3) return false;

  const allMarked = plannedInWeek.every((date) =>
    applications.some((a) => a.date === date && a.marked)
  );

  return allMarked;
}

/**
 * ✅ НОВОЕ: Подсчитывает количество идеальных недель за всё время
 */
export function checkConsecutivePerfectWeeks(
  plannedDates: string[],
  applications: Array<{ date: string; marked: boolean }>,
  target: number,
  timezone: string = 'UTC'
): boolean {
  if (plannedDates.length === 0) return false;

  // Группируем применения по неделям
  const weekMap = new Map<string, { planned: string[]; marked: string[] }>();

  for (const date of plannedDates) {
    const d = new Date(date);
    const weekKey = getWeekKey(d);

    if (!weekMap.has(weekKey)) {
      weekMap.set(weekKey, { planned:[], marked:[] });
    }
    weekMap.get(weekKey)!.planned.push(date);

    const app = applications.find((a) => a.date === date && a.marked);
    if (app) {
      weekMap.get(weekKey)!.marked.push(date);
    }
  }

  // Считаем последовательные идеальные недели
  const sortedWeeks = Array.from(weekMap.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  let consecutivePerfect = 0;

  for (const[, week] of sortedWeeks) {
    if (week.planned.length >= 3 && week.planned.length === week.marked.length) {
      consecutivePerfect++;
    } else if (week.marked.length > 0) {
      // Если неделя не идеальна но есть отметки - прерываем подсчёт
      break;
    }
  }

  return consecutivePerfect >= target;
}

/**
 * ✅ НОВОЕ: Подсчитывает количество камбэков
 * Камбэк = возвращение к применениям после пропуска
 */
export function checkComebackCount(
  plannedDates: string[],
  applications: Array<{ date: string; marked: boolean }>,
  target: number,
  timezone: string = 'UTC'
): boolean {
  if (plannedDates.length < 2) return false;

  const today = getDateInUserTimezone(new Date(), timezone); // ✅ FIX: используем user timezone
  const sortedPlanned = [...plannedDates].filter((d) => d <= today).sort();

  let comebacks = 0;
  let hadMiss = false;

  for (const date of sortedPlanned) {
    const isMarked = applications.some((a) => a.date === date && a.marked);

    if (!isMarked) {
      hadMiss = true;
    } else if (hadMiss) {
      // Отметка после пропуска = камбэк
      comebacks++;
      hadMiss = false;
    }
  }

  return comebacks >= target;
}

/** Вспомогательная: получить ключ недели (год-неделя) */
function getWeekKey(date: Date): string {
  const year = date.getFullYear();
  const weekNum = getWeekNumber(date);
  return `${year}-W${String(weekNum).padStart(2, '0')}`;
}

/** Вспомогательная: номер недели в году */
function getWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

/**
 * ✅ Проверяет завершение полного курса
 * Разблокируется когда ВСЕ запланированные даты отмечены
 * Это максимальное достижение - "Легенда ухода"
 */
export function checkCompleteCourse(
  plannedDates: string[],
  applications: Array<{ date: string; marked: boolean }>
): boolean {
  // Должен быть хотя бы 1 запланированный день
  if (plannedDates.length === 0) return false;

  // ✅ ИСПРАВЛЕНО: Проверяем что отмечена ПОСЛЕДНЯЯ запланированная дата
  // "Легенда ухода" выдается только после завершения ВСЕГО курса (последний день отмечен)
  const lastPlannedDate = plannedDates.sort().reverse()[0]; // Самая поздняя дата
  const lastDateMarked = applications.some(
    (app) => app.date === lastPlannedDate && app.marked
  );

  return lastDateMarked;
}
